import React from 'react';
import { RiBuilding2Line } from 'react-icons/ri';
import { getSingleSupplierDetails } from '../../../../../Actions/supplierActions';
import { useQuery } from '@tanstack/react-query';
import SupplierSetupDetailsLoader from "./SupplierSetupDetailsLoader"
import EditSupplierInfo from "./EditSupplierInfo"
import { Divider } from '../../../../../components/UiComponents/Ui';
import InformationTile from "../../../../../components/UiComponents/InformationTile"
import { getFormattedDateWithTime } from '../../../../../Utlils';
import EditSupplierBankInfo from "./EditSupplierBankInfo"
import EditLocationInfo from "./EditLocationInfo"
import EditBusinessIDsModal from "./EditBusinessIDsModal"

const SetupTab = ({ supplierID }) => {
  const {
    data: supplierDetails,
    isLoading: supplierDetailsLoading,
    error: supplierDetailsError,
    refetch:supplierDetailsMutate
  } = useQuery({
    queryKey: ['supplier-details', supplierID],
    queryFn: () => getSingleSupplierDetails({ supplierID }),
    enabled: !!supplierID,
  });

//   console.log("supplierDetails=>",supplierDetails);
  

  return supplierDetailsLoading ? (
    <SupplierSetupDetailsLoader />
  ) : (
    <>
      <div className="flex justify-between items-start gap-6">
        <div className="space-y-4 w-[50%]">
          {/* Supplier information */}
          <div className="px-6 py-3 space-y-3 shadow-cardShadow bg-[#FFFFFF] rounded-xl ">
            <header className="flex justify-between items-center">
              <div className="space-y-1">
                <h2 className="text-md font-bold text-gray-900">
                  Supplier Information
                </h2>
                <span className="text-gray-500 text-xs font-[400]">
                  Detailed information about the Supplier.
                </span>
              </div>
                <EditSupplierInfo
                  supplierID={supplierID}
                  supplierDetailsMutate={supplierDetailsMutate}
                  supplierDetails={{
                    supplierName: supplierDetails?.supplierName,
                    email: supplierDetails?.email,
                    phone: supplierDetails?.phone,
                    description: supplierDetails?.description,
                    currency: supplierDetails?.currency,
                    invoicePrefix: supplierDetails?.invoicePrefix,
                  }}
                />

            </header>
            <Divider />
            <div className="space-y-[8px]">
              <div>
                <InformationTile
                  rowLabel={'Supplier Name'}
                  rowValue={supplierDetails?.supplierName}
                />
                <InformationTile
                  rowLabel="Email"
                  rowValue={supplierDetails?.email}
                />
                <InformationTile
                  rowLabel="Contact no."
                  rowValue={
                    supplierDetails?.phone
                      ? `${supplierDetails?.phone?.countryCode} ${supplierDetails?.phone?.number}`
                      : '-'
                  }
                />
                <InformationTile
                  rowLabel="Currency"
                  rowValue={`${supplierDetails?.currency?.label} (${supplierDetails?.currency?.symbol})`}
                />
                <InformationTile
                  rowLabel="Description"
                  rowValue={supplierDetails?.description || '-'}
                />{' '}
                <InformationTile
                  rowLabel="Invoice Prefix"
                  rowValue={supplierDetails?.invoicePrefix || '-'}
                />
              </div>
              <Divider />
              <div>
                <InformationTile
                  rowLabel="Created By"
                  rowValue={`${supplierDetails?.createdBy?.personalDetails?.fullName}`}
                />
                <InformationTile
                  rowLabel="Created At"
                  rowValue={
                    getFormattedDateWithTime(supplierDetails?.createdAt) || '-'
                  }
                />
              </div>
            </div>
          </div>

          {/* banking information */}
          <div className="px-6 py-3 space-y-3 shadow-cardShadow bg-[#FFFFFF] rounded-xl">
            <header className="flex justify-between items-center">
              <div className="space-y-1">
                <h2 className="text-md font-bold text-gray-900">
                  Banking Information
                </h2>
                <span className="text-gray-500 text-xs font-[400]">
                  Detailed banking information of the Supplier.
                </span>
              </div>
                <EditSupplierBankInfo
                  supplierID={supplierID}
                  bankDetails={supplierDetails?.bankAccounts}
                  supplierDetailsMutate={supplierDetailsMutate}
                />
            </header>
            <Divider />

            <div className="max-h-[300px] overflow-y-auto containerScroll pr-1">
              {(supplierDetails?.bankAccounts || [])?.length > 0 ? (
                <div className="space-y-3">
                  {(supplierDetails?.bankAccounts || []).map(
                    (account, index) => (
                      <div
                        key={account._id || index}
                        className="flex items-start gap-3 border border-gray-100 rounded-xl p-3 bg-white shadow-sm"
                      >
                        {/* Icon */}
                        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                          <RiBuilding2Line className="w-5 h-5 text-blue-600" />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          {/* Top row: name + badge */}
                          <div className="flex items-center justify-between gap-2 mb-0.5">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {account.accountHolderName || '-'}
                            </p>
                            {account.bankType && (
                              <span className="flex-shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-md bg-green-50 text-green-700">
                                {account.bankType}
                              </span>
                            )}
                          </div>

                          {/* Bank name + account type */}
                          <p className="text-xs text-gray-500 mb-2">
                            {account.bankName || '-'}
                          </p>

                          {/* Key fields row */}
                          <div className="flex flex-wrap gap-x-5 gap-y-1.5">
                            <div className="flex flex-col gap-0.5">
                              <span className="text-[11px] text-gray-400">
                                Account no.
                              </span>
                              <span className="text-xs text-gray-800 font-mono">
                                {account.accountNumber || '-'}
                              </span>
                            </div>

                            {account.ifscCode && (
                              <div className="flex flex-col gap-0.5">
                                <span className="text-[11px] text-gray-400">
                                  IFSC
                                </span>
                                <span className="text-xs text-gray-800 font-mono">
                                  {account.ifscCode}
                                </span>
                              </div>
                            )}

                            {account.swiftBic && (
                              <div className="flex flex-col gap-0.5">
                                <span className="text-[11px] text-gray-400">
                                  SWIFT / BIC
                                </span>
                                <span className="text-xs text-gray-800 font-mono">
                                  {account.swiftBic}
                                </span>
                              </div>
                            )}

                            {account.ibanNumber && (
                              <div className="flex flex-col gap-0.5">
                                <span className="text-[11px] text-gray-400">
                                  IBAN
                                </span>
                                <span className="text-xs text-gray-800 font-mono">
                                  {account.ibanNumber}
                                </span>
                              </div>
                            )}

                            {account.routingNumber && (
                              <div className="flex flex-col gap-0.5">
                                <span className="text-[11px] text-gray-400">
                                  Routing no.
                                </span>
                                <span className="text-xs text-gray-800 font-mono">
                                  {account.routingNumber}
                                </span>
                              </div>
                            )}

                            {account.nationalBankCode && (
                              <div className="flex flex-col gap-0.5">
                                <span className="text-[11px] text-gray-400">
                                  National code
                                </span>
                                <span className="text-xs text-gray-800 font-mono">
                                  {account.nationalBankCode}
                                </span>
                              </div>
                            )}

                            {account.bankAddress && (
                              <div className="flex flex-col gap-0.5">
                                <span className="text-[11px] text-gray-400">
                                  Bank address
                                </span>
                                <span className="text-xs text-gray-800">
                                  {[
                                    account.bankAddress.city,
                                    account.bankAddress.state?.label,
                                    account.bankAddress.country?.label,
                                  ]
                                    .filter(Boolean)
                                    .join(', ')}
                                  {account.bankAddress.postalCode
                                    ? ` — ${account.bankAddress.postalCode}`
                                    : ''}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              ) : (
                <div className="py-6 text-center text-gray-400 text-sm">
                  No banking information available.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-4 w-[50%]">
          {/* locations */}
          <div className="px-6 py-3 space-y-3 shadow-cardShadow bg-[#FFFFFF] rounded-xl">
            <header className="flex justify-between items-center">
              <div className="space-y-1">
                <h2 className="text-md font-bold text-gray-900">Locations</h2>
                <span className="text-gray-500 text-xs font-[400]">
                  Addresses and tax IDs associated with this supplier.
                </span>
              </div>
                <EditLocationInfo
                  supplierID={supplierID}
                  locationDetails={supplierDetails?.locations}
                  supplierDetailsMutate={supplierDetailsMutate}
                />
            </header>
            <Divider />
            <div className="space-y-3 max-h-[400px] containerScroll overflow-y-auto pr-1">
              {supplierDetails?.locations?.length ? (
                supplierDetails.locations.map((location, index) => (
                  <LocationBlock
                    key={location._id}
                    index={index}
                    location={location}
                    isLast={
                      index === (supplierDetails?.locations || [])?.length - 1
                    }
                  />
                ))
              ) : (
                <span className="text-sm text-gray-400 italic">
                  No locations added.
                </span>
              )}
            </div>
          </div>

          <div className="px-6 py-3 space-y-3 shadow-cardShadow bg-[#FFFFFF] rounded-xl">
            <header className="flex justify-between items-center">
              <div className="space-y-1">
                <h2 className="text-md font-bold text-gray-900">
                  Business IDs
                </h2>
                <span className="text-gray-500 text-xs font-[400]">
                  Business identifiers for this client.
                </span>
              </div>
                <EditBusinessIDsModal
                  supplierDetails={{
                    companyCodes: supplierDetails?.companyCodes || [],
                    _id: supplierDetails?._id,
                  }}
                  supplierDetailsMutate={supplierDetailsMutate}
                />
            </header>
            <Divider />
            <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1 containerScroll">
              {supplierDetails?.companyCodes?.length ? (
                supplierDetails.companyCodes.map((d, i) => {
                  return (
                    <div
                      className="px-4 py-2 border rounded-lg flex justify-between items-center"
                      key={i}
                    >
                      <p className="text-gray-500 text-sm font-regular">
                        {d.label}
                      </p>
                      <p className="text-gray-800 text-sm text-right font-medium w-[60%] line-clamp-1">
                        {d.value}
                      </p>
                    </div>
                  );
                })
              ) : (
                <div className="px-4 py-2 border rounded-lg flex justify-between items-center">
                  <p className="text-gray-500 text-sm font-regular">
                    No business IDs added!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const TYPE_STYLES = {
  headquarters: {
    icon: '🏢',
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-700',
    badgeBg: 'bg-blue-50',
    badgeColor: 'text-blue-800',
  },
  warehouse: {
    icon: '🏭',
    iconBg: 'bg-green-50',
    iconColor: 'text-green-700',
    badgeBg: 'bg-green-50',
    badgeColor: 'text-green-800',
  },
  branch: {
    icon: '🏛️',
    iconBg: 'bg-purple-50',
    iconColor: 'text-purple-700',
    badgeBg: 'bg-purple-50',
    badgeColor: 'text-purple-800',
  },
  remote: {
    icon: '🏠',
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-700',
    badgeBg: 'bg-amber-50',
    badgeColor: 'text-amber-800',
  },
};

const FALLBACK_STYLE = {
  icon: '📍',
  iconBg: 'bg-gray-100',
  iconColor: 'text-gray-600',
  badgeBg: 'bg-gray-100',
  badgeColor: 'text-gray-700',
};

const LocationBlock = ({
  location,
  index,
  isLast,
}) => {
  const { address, taxIds, locationType } = location;
  const style = TYPE_STYLES[locationType] ?? FALLBACK_STYLE;

  const addressLines = [
    address?.street1,
    address?.street2,
    [address?.city, address?.state?.label].filter(Boolean).join(', '),
  ].filter(Boolean);

  const countryLine = [address?.country?.label, address?.postalCode]
    .filter(Boolean)
    .join(' — ');

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <div
            className={`w-7 h-7 rounded-lg ${style.iconBg} flex items-center justify-center text-base leading-none`}
          >
            <span role="img" aria-hidden="true">
              {style.icon}
            </span>
          </div>
          <span className="text-sm font-medium text-gray-900">
            {address?.city || `Location ${index + 1}`}
          </span>
        </div>
        <span
          className={`text-[11px] font-medium px-2.5 py-0.5 rounded-full ${style.badgeBg} ${style.badgeColor}`}
        >
          {locationType}
        </span>
      </div>

      {/* Body */}
      <div className="px-3.5 py-2 flex flex-col">
        {/* Address row */}
        <div className="flex justify-between items-start gap-3 py-2">
          <span className="text-xs text-gray-500 pt-0.5 min-w-[56px] flex-shrink-0">
            Address
          </span>
          {addressLines.length ? (
            <div className="text-right">
              {addressLines.map((line, i) => (
                <p
                  key={i}
                  className="text-[13px] font-medium text-gray-900 leading-[1.65] m-0"
                >
                  {line}
                </p>
              ))}
              {countryLine && (
                <p className="text-[11px] text-gray-400 leading-[1.65] m-0">
                  {countryLine}
                </p>
              )}
            </div>
          ) : (
            <span className="text-[13px] text-gray-300 italic">—</span>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100" />

        {/* Tax IDs row */}
        <div className="flex justify-between items-center gap-3 py-2">
          <span className="text-xs text-gray-500 min-w-[56px] flex-shrink-0">
            Tax IDs
          </span>
          {taxIds?.length ? (
            <div className="flex flex-wrap justify-end gap-1">
              {taxIds.map((tax) => (
                <span
                  key={tax.value}
                  className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 border border-gray-200"
                >
                  {tax.label}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-[13px] text-gray-300 italic">—</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetupTab;
